/**
 * Lightweight client-side logger for diagnosing admin auth/elevation bounces.
 *
 * Every call emits a single console line prefixed with `[admin-guard]` plus a
 * high-resolution timestamp (ms since page load) and a free-form payload.
 * Logs are intentionally cheap and always-on so production bounce reports can
 * be investigated by asking a user to copy/paste their console.
 *
 * Disable in a given session by running in the console:
 *   localStorage.setItem("cpaf_admin_debug", "off")
 */
const NS = "[admin-guard]";

function enabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem("cpaf_admin_debug") !== "off";
  } catch {
    return true;
  }
}

function ts(): string {
  if (typeof performance !== "undefined" && performance.now) {
    return `t+${performance.now().toFixed(1)}ms`;
  }
  return new Date().toISOString();
}

export function adminLog(event: string, payload?: Record<string, unknown>): void {
  if (!enabled()) return;
  // eslint-disable-next-line no-console
  console.log(`${NS} ${ts()} ${event}`, payload ?? {});
}

export function adminWarn(event: string, payload?: Record<string, unknown>): void {
  if (!enabled()) return;
  // eslint-disable-next-line no-console
  console.warn(`${NS} ${ts()} ${event}`, payload ?? {});
}

/**
 * Time a single async operation and log start + end with duration.
 * Returns the operation's resolved value (or rethrows its error after logging).
 */
export async function adminTimed<T>(
  event: string,
  fn: () => Promise<T>,
  extra: Record<string, unknown> = {},
): Promise<T> {
  const start = typeof performance !== "undefined" ? performance.now() : Date.now();
  adminLog(`${event}:start`, extra);
  try {
    const result = await fn();
    const end = typeof performance !== "undefined" ? performance.now() : Date.now();
    adminLog(`${event}:end`, {
      ...extra,
      durationMs: +(end - start).toFixed(1),
      result,
    });
    return result;
  } catch (err) {
    const end = typeof performance !== "undefined" ? performance.now() : Date.now();
    adminWarn(`${event}:error`, {
      ...extra,
      durationMs: +(end - start).toFixed(1),
      error: err instanceof Error ? err.message : String(err),
    });
    throw err;
  }
}
